import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { inr, apiError } from '../utils/format';
import { packsOf } from '../utils/packs';
import { LabelsModal } from './Products';

// Warehouse-facing label printing: one row per barcoded pack. Pick a pack and print
// its barcode labels using the saved design. Reuses the Products LabelsModal.
const Labels = () => {
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [labels, setLabels] = useState(null);

  useEffect(() => {
    api.get('/products').then(({ data }) => setProducts(data.products || []))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  // Flatten to one printable row per barcoded variant so each pack is its own line.
  const rows = useMemo(() => {
    const out = [];
    for (const p of products) for (const v of packsOf(p)) if (v.barcode) out.push({ product: p, variant: v });
    const q = query.trim().toLowerCase();
    if (!q) return out;
    return out.filter(({ product, variant }) => [product.name, product.code, product.category, product.brand, variant.weightLabel, variant.barcode, variant.sku].some((x) => String(x || '').toLowerCase().includes(q)));
  }, [products, query]);

  return (
    <div>
      <div className="section-head"><span className="muted small">{rows.length} packs</span></div>
      <p className="muted small" style={{ marginTop: '-.4rem' }}>Pick a pack to print its barcode labels using the saved design. You only choose the manufacturing date and how many copies — the label size, layout and contents come from <Link to="/settings">Settings → Barcode label design</Link>.</p>
      <div className="flex gap" style={{ marginBottom: '1rem', alignItems: 'center' }}>
        <input placeholder="Search product, code, pack, barcode, category…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 360 }} />
        {query && <button className="btn btn-outline btn-sm" onClick={() => setQuery('')}>Clear</button>}
      </div>
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th className="right">ID</th><th>Product</th><th>Category</th><th>Pack</th><th className="right">Price</th><th>Barcode</th><th></th></tr></thead>
            <tbody>
              {rows.map(({ product, variant }) => (
                <tr key={variant.id}>
                  <td className="right mono muted">{product.id}</td>
                  <td className="link-strong">{product.name}</td>
                  <td className="muted">{product.category || '—'}</td>
                  <td>{variant.weightLabel || '—'}</td>
                  <td className="right muted">{Number(variant.sellingPrice) > 0 ? inr(variant.sellingPrice) : '—'}</td>
                  <td className="mono muted">{variant.barcode}</td>
                  <td><button className="btn btn-primary btn-sm" onClick={() => setLabels({ ...product, variants: [variant] })}>Print</button></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7} className="empty">{query ? 'No packs match your search.' : 'No packs with barcodes to label.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {labels && <LabelsModal product={labels} onClose={() => setLabels(null)} />}
    </div>
  );
};

export default Labels;
